import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ElementPreviewFactoryProps } from '../ElementPreviewFactory';
import useAuth from '@/data/hook/useAuth';
import { isStudent } from '@/functions/auth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  codeEditorActivitiesQueryKeys,
  codeEditorProgressQueryKeys,
} from '@/data/services/querykeys';
import ActivityHeading from '../ActivityHeading';
import { CodeEditorActivity } from '@/models/CodeEditorActivity';
import CodeEditor from '@/components/codeEditor/CodeEditor';
import { useTranslation } from 'react-i18next';
import { useAnswerDebounce } from '../ActivitiesPreview.hooks';
import { updateCodeEditorProgress } from '@/data/services/activityElement/codeEditorActivityProgressServices';
import { ActivityTypeEnum } from '@/models/_deprecated/ActivityElement';
import alert from '@/utils/shared/alert';
import { getErrorMessage } from '@/utils/getErrorMessage';
import useActivityView from '@/data/hook/useActivityView';
import Tag from '@/components/ui/custom/dataDisplay/Tag';
import LoadingView from '@/pages/courses/LoadingView';
import ErrorComponent from '@/components/ui/custom/ErrorComponent';
import CodeValidation from './validation/CodeValidation';

function CodeEditorActivityElement({
  progressId,
  questionId,
  activityMode,
  attemptElement,
}: ElementPreviewFactoryProps) {
  const { user } = useAuth();

  const studentView = isStudent(user?.userType);

  if (studentView) {
    return (
      <StudentView
        key={progressId}
        progressId={progressId}
        questionId={questionId}
        activityMode={activityMode}
        attemptElement={attemptElement}
      />
    );
  }

  return <StaffView key={questionId} questionId={questionId} />;
}

function StudentView({
  progressId,
  questionId: activityId,
  attemptElement,
}: Pick<
  ElementPreviewFactoryProps,
  'questionId' | 'progressId' | 'activityMode' | 'attemptElement'
>) {
  const [value, setValue] = useState<string>();
  const [codeToValidate, setCodeToValidate] = useState<string>('');

  const {
    invalidate,
    blockStepNavigate,
    mode,
    finishObserver: resetObserver,
  } = useActivityView();
  const { queryFn, queryKey } = codeEditorProgressQueryKeys.get({
    progressId,
    activityId,
  });

  const {
    data: codeEditorProgress,
    isInitialLoading: isLoading,
    error,
  } = useQuery({
    queryFn,
    queryKey,
    enabled: mode === 'activity' || mode === 'answerkey',
  });

  const queryClient = useQueryClient();
  const subscribeIdRef = useRef<string>();

  const { debounce } = useAnswerDebounce(500);

  const saveAnswer = () => {
    blockStepNavigate(true);
    return updateCodeEditorProgress(
      { activityId, progressId },
      { answer: value, isDone: true },
    );
  };

  const { mutate: updateAnswer } = useMutation(saveAnswer, {
    async onSuccess(data) {
      queryClient.setQueryData(queryKey, data);
      await invalidate(ActivityTypeEnum.CODE_EDITOR, data);
    },
    onError(error: any) {
      alert.error(getErrorMessage(error));
    },
    onSettled() {
      blockStepNavigate(false);
    },
  });

  const activity = codeEditorProgress?.codeEditorActivity;

  const onAnswer = useCallback(
    (value: string | undefined) => {
      setValue(value || '');
      debounce(updateAnswer);
    },
    [debounce, updateAnswer],
  );

  const handleValidateCode = useCallback(() => {
    setCodeToValidate(value || '');
  }, [value]);

  useEffect(() => {
    if (typeof value !== 'undefined') return;

    if (
      mode === 'review' &&
      attemptElement?.type === ActivityTypeEnum.CODE_EDITOR
    ) {
      return setValue(attemptElement.progress.answer || '');
    }

    if (
      mode === 'answerkey' &&
      attemptElement?.type === ActivityTypeEnum.CODE_EDITOR
    ) {
      return setValue(attemptElement.progress.answer || '');
    }

    if (codeEditorProgress?.answer) return setValue(codeEditorProgress?.answer);

    if (activity?.initialCode) return onAnswer(activity.initialCode);
  }, [
    activity?.initialCode,
    codeEditorProgress?.answer,
    mode,
    onAnswer,
    attemptElement,
    value,
  ]);

  useEffect(() => {
    if (subscribeIdRef.current || !codeEditorProgress) return;

    subscribeIdRef.current = resetObserver.subscribe(() => {
      queryClient.setQueryData(queryKey, {
        ...codeEditorProgress,
        answer: null,
      });
    });
  }, [resetObserver, queryClient, queryKey, codeEditorProgress]);

  return (
    <ActivityDisplay isLoading={isLoading} error={error} activity={activity}>
      {activity?.language ? (
        <CodeEditor
          language={activity.language}
          value={value}
          key={codeEditorProgress?.id}
          onChangeCode={onAnswer}
          readOnly={mode !== 'activity'}
        />
      ) : null}

      {activity?.language && (
        <CodeValidation
          activityId={activityId}
          progressId={progressId}
          disabled={!value}
          sourceCode={codeToValidate}
          language={activity?.language}
          onValidateRequest={handleValidateCode}
        />
      )}
    </ActivityDisplay>
  );
}

type StaffViewProps = {
  questionId: number;
};

function StaffView({ questionId }: StaffViewProps) {
  const {
    data: codeEditorActivity,
    isLoading,
    error,
  } = useQuery(codeEditorActivitiesQueryKeys.get(questionId));

  const initialCode = codeEditorActivity?.initialCode;
  const language = codeEditorActivity?.language;

  return (
    <ActivityDisplay
      activity={codeEditorActivity}
      isLoading={isLoading}
      error={error}
    >
      {language ? (
        <CodeEditor
          key={codeEditorActivity.id}
          language={language}
          value={initialCode ?? ''}
        />
      ) : null}
    </ActivityDisplay>
  );
}

type ActivityDisplayProps = {
  activity?: CodeEditorActivity;
  isLoading?: boolean;
  error?: unknown;
};

const ActivityDisplay: React.FC<PropsWithChildren<ActivityDisplayProps>> = ({
  activity,
  error,
  isLoading,
  children,
}) => {
  const [t] = useTranslation('translation', {
    keyPrefix: 'codeEditorLanguage',
  });
  const errorDetail = getErrorMessage(error);

  if (isLoading && !activity) {
    return <LoadingView />;
  }
  if (error || !activity) {
    return <ErrorComponent errorTextTitle={errorDetail} />;
  }

  return (
    <div className="flex flex-col gap-1 w-11/12">
      <ActivityHeading
        heading={activity.title}
        subheading={activity.subtitle}
        content={activity.content}
      />
      <Tag
        text={t(activity.language)}
        className={{ base: 'border border-primary' }}
      />
      {children}
    </div>
  );
};

export default CodeEditorActivityElement;
